import React, { useCallback, useEffect, useState } from 'react';

import {
    CheckCircleIcon,
    BuildingsIcon,
    CubeIcon,
    CaretDownIcon,
    CaretUpIcon,
    ListIcon,
    XIcon,
} from '@phosphor-icons/react';

import { EditorIconTooltip } from '../../EditorIconTooltip';
import type { DashboardSectionNavItem, DashboardSectionPanelId } from '../dashboardSectionNav';

import {
    openSiteViewer3dFromDashboard,
    showSiteViewerUnavailableModal,
    useSiteViewer3dBlocked,
} from '../../../../../helpers/siteViewer3dAccess';

export interface DashboardSectionSlideRailProps {
    sectionRailExpanded: boolean;
    setSectionRailExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    railNavItems: ReadonlyArray<DashboardSectionNavItem>;
    dashboardSectionPanel: DashboardSectionPanelId;
    setDashboardSectionPanel: React.Dispatch<React.SetStateAction<DashboardSectionPanelId>>;
    opportunityID: any;
    showAhjTool: boolean;
    setOppValidationModalOpen: (next: boolean) => void;
    setAhjModalOpen: (next: boolean) => void;
    setIsDashboardOpen: (next: boolean | ((prev: boolean) => boolean)) => void;
    /** Fired when AI Designs is chosen on the rail (inline panel — not the modal). */
    onDesignsRailSelect?: () => void;
}

/**
 * Floating left “slide” rail: collapsed = 3D + full dashboard + designs + opp validation;
 * expanded = labeled section list from mockup.
 * Mobile (≤820px): FAB + floating vertical menu panel.
 */
export const DashboardSectionSlideRail: React.FC<DashboardSectionSlideRailProps> = ({
    sectionRailExpanded,
    setSectionRailExpanded,
    railNavItems,
    dashboardSectionPanel,
    setDashboardSectionPanel,
    opportunityID,
    showAhjTool,
    setOppValidationModalOpen,
    setAhjModalOpen,
    setIsDashboardOpen,
    onDesignsRailSelect,
}) => {
    const { blocked: viewer3dBlocked, message: blockedMessage } = useSiteViewer3dBlocked();

    const [isPortraitTabletOrMobile, setIsPortraitTabletOrMobile] = useState(false);

    const onView3dDesignClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            e.stopPropagation();
            if (viewer3dBlocked) {
                showSiteViewerUnavailableModal(blockedMessage);
                return;
            }
            openSiteViewer3dFromDashboard(setIsDashboardOpen);
        },
        [viewer3dBlocked, blockedMessage, setIsDashboardOpen]
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 820px)');
        const applyViewportState = () => {
            setIsPortraitTabletOrMobile(mediaQuery.matches);
        };

        applyViewportState();
        mediaQuery.addEventListener('change', applyViewportState);
        return () => {
            mediaQuery.removeEventListener('change', applyViewportState);
        };
    }, []);

    useEffect(() => {
        if (!isPortraitTabletOrMobile) {
            document.documentElement.removeAttribute('data-obd-mobile-section-rail');
            return;
        }
        document.documentElement.setAttribute(
            'data-obd-mobile-section-rail',
            sectionRailExpanded ? 'open' : 'closed'
        );
        return () => {
            document.documentElement.removeAttribute('data-obd-mobile-section-rail');
        };
    }, [isPortraitTabletOrMobile, sectionRailExpanded]);

    const mobileMenuOpen = isPortraitTabletOrMobile && sectionRailExpanded;

    const closeMobileMenu = useCallback(() => {
        setSectionRailExpanded(false);
    }, [setSectionRailExpanded]);

    const toggleMobileMenu = useCallback(() => {
        setSectionRailExpanded((v) => !v);
    }, [setSectionRailExpanded]);

    const showCollapsedOppValidation =
        !isPortraitTabletOrMobile && !sectionRailExpanded && Boolean(opportunityID);

    const showExpandedDesignQuickTools =
        sectionRailExpanded && (opportunityID || showAhjTool);

    const viewer3dLabel = viewer3dBlocked
        ? '3D design unavailable — click for details'
        : 'View 3D design';

    const expandLabel = sectionRailExpanded ? 'Show fewer sections' : 'Show all sections';

    const wrapRailTooltip = (label: string, button: React.ReactElement) => (
        <EditorIconTooltip title={label} zone="sidebar">
            <span className="editor-icon-tooltip__hit obd-dashboard-section-rail__tooltip-hit">{button}</span>
        </EditorIconTooltip>
    );

    const renderNavButton = (item: DashboardSectionNavItem) => {
        const Icon = item.Icon;
        const active = dashboardSectionPanel === item.id;
        const iconWeight = active ? 'bold' : 'regular';

        return wrapRailTooltip(
            item.label,
            <button
                type="button"
                className={`obd-dashboard-section-rail__btn${item.railVariant === 'card' ? ' obd-dashboard-section-rail__btn--card' : ''}${active ? ' obd-dashboard-section-rail__btn--active' : ''}`}
                aria-label={item.label}
                aria-current={active ? 'true' : undefined}
                onClick={() => {
                    if (item.id === 'designs') {
                        onDesignsRailSelect?.();
                        setDashboardSectionPanel('designs');
                        if (isPortraitTabletOrMobile) {
                            closeMobileMenu();
                        }
                        return;
                    }
                    setDashboardSectionPanel(item.id);
                    if (isPortraitTabletOrMobile) {
                        closeMobileMenu();
                    }
                }}
            >
                <span className="obd-dashboard-section-rail__icon-wrap" aria-hidden>
                    <Icon size={19} weight={iconWeight} />
                </span>
            </button>
        );
    };

    const renderRailMenuScroll = () => (
        <div className="obd-dashboard-section-rail__scroll" role="group" aria-label="Dashboard section tools">
            {wrapRailTooltip(
                viewer3dLabel,
                <button
                    type="button"
                    className={`obd-dashboard-section-rail__btn obd-dashboard-section-rail__btn--viewer${viewer3dBlocked ? ' obd-dashboard-section-rail__btn--blocked' : ''}`}
                    aria-label={viewer3dLabel}
                    onClick={onView3dDesignClick}
                >
                    <span className="obd-dashboard-section-rail__icon-wrap" aria-hidden>
                        <CubeIcon size={19} weight="regular" />
                    </span>
                </button>
            )}

            {railNavItems
                .filter((item) => !item.hideFromRail)
                .map((item) => (
                    <React.Fragment key={item.id}>
                        {item.railSepBefore ? <div className="obd-dashboard-section-rail__sep" aria-hidden /> : null}
                        {renderNavButton(item)}
                        {item.railSepAfter ? <div className="obd-dashboard-section-rail__sep" aria-hidden /> : null}
                        {sectionRailExpanded &&
                        item.id === 'designs' &&
                        showExpandedDesignQuickTools &&
                        (opportunityID || showAhjTool) ? (
                                <div
                                    className="obd-dashboard-section-rail__quick-tools"
                                    role="group"
                                    aria-label="Design quick tools"
                                >
                                    {opportunityID
                                        ? wrapRailTooltip(
                                              'Opportunity Validation',
                                              <button
                                                  type="button"
                                                  className="obd-dashboard-section-rail__btn"
                                                  aria-label="Opportunity Validation"
                                                  onClick={() => setOppValidationModalOpen(true)}
                                              >
                                                  <span
                                                      className="obd-dashboard-section-rail__icon-wrap"
                                                      aria-hidden
                                                  >
                                                      <CheckCircleIcon size={19} weight="regular" />
                                                  </span>
                                              </button>
                                          )
                                        : null}
                                    {showAhjTool
                                        ? wrapRailTooltip(
                                              'AHJ rules and setbacks',
                                              <button
                                                  type="button"
                                                  className="obd-dashboard-section-rail__btn obd-dashboard-section-rail__btn--ahj"
                                                  aria-label="Show AHJ rules"
                                                  onClick={() => setAhjModalOpen(true)}
                                              >
                                                  <span
                                                      className="obd-dashboard-section-rail__icon-wrap"
                                                      aria-hidden
                                                  >
                                                      <BuildingsIcon size={19} weight="regular" />
                                                  </span>
                                              </button>
                                          )
                                            : null}
                                </div>
                        ) : null}
                    </React.Fragment>
                ))}

            {showCollapsedOppValidation ? (
                <>
                    <div className="obd-dashboard-section-rail__sep" aria-hidden />
                    {wrapRailTooltip(
                        'Opportunity Validation',
                        <button
                            type="button"
                            className="obd-dashboard-section-rail__btn obd-dashboard-section-rail__btn--quick"
                            aria-label="Opportunity Validation"
                            onClick={() => setOppValidationModalOpen(true)}
                        >
                            <span className="obd-dashboard-section-rail__icon-wrap" aria-hidden>
                                <CheckCircleIcon size={19} weight="regular" />
                            </span>
                        </button>
                    )}
                </>
            ) : null}

            {!isPortraitTabletOrMobile && sectionRailExpanded ? (
                <div className="obd-dashboard-section-rail__sep" aria-hidden />
            ) : null}
        </div>
    );

    if (isPortraitTabletOrMobile) {
        return (
            <>
                {mobileMenuOpen ? (
                    <button
                        type="button"
                        className="obd-dashboard-section-rail__mobile-backdrop"
                        aria-label="Close section menu"
                        onClick={closeMobileMenu}
                    />
                ) : null}

                <nav
                    className={`obd-dashboard-section-rail obd-dashboard-section-rail--floating obd-dashboard-section-rail--mobile-floating-host${mobileMenuOpen ? ' obd-dashboard-section-rail--mobile-menu-open' : ''}`}
                    aria-label="Dashboard sections and quick tools"
                >
                    {wrapRailTooltip(
                        mobileMenuOpen ? 'Close section menu' : 'Open section menu',
                        <button
                            type="button"
                            className={`obd-dashboard-section-rail__btn obd-dashboard-section-rail__btn--mobile-menu-fab${mobileMenuOpen ? ' obd-dashboard-section-rail__btn--active' : ''}`}
                            aria-label={mobileMenuOpen ? 'Close section menu' : 'Open section menu'}
                            aria-expanded={mobileMenuOpen}
                            aria-haspopup="menu"
                            onClick={toggleMobileMenu}
                        >
                            <span className="obd-dashboard-section-rail__icon-wrap" aria-hidden>
                                {mobileMenuOpen ? (
                                    <XIcon size={22} weight="bold" />
                                ) : (
                                    <ListIcon size={22} weight="bold" />
                                )}
                            </span>
                        </button>
                    )}

                    {mobileMenuOpen ? (
                        <div
                            className="obd-dashboard-section-rail__mobile-menu-panel"
                            role="menu"
                            aria-label="Dashboard sections"
                        >
                            <div className="obd-dashboard-section-rail__inner">{renderRailMenuScroll()}</div>
                        </div>
                    ) : null}
                </nav>
            </>
        );
    }

    return (
        <nav
            className={`obd-dashboard-section-rail obd-dashboard-section-rail--floating${sectionRailExpanded ? ' obd-dashboard-section-rail--expanded' : ' obd-dashboard-section-rail--collapsed'}`}
            aria-label="Dashboard sections and quick tools"
        >
            <div className="obd-dashboard-section-rail__inner">
                {renderRailMenuScroll()}

                {wrapRailTooltip(
                    expandLabel,
                    <button
                        type="button"
                        className="obd-dashboard-section-rail__btn obd-dashboard-section-rail__btn--expand"
                        aria-label={
                            sectionRailExpanded
                                ? 'Collapse section navigation'
                                : 'Expand section navigation'
                        }
                        aria-expanded={sectionRailExpanded}
                        onClick={() => setSectionRailExpanded((v) => !v)}
                    >
                        <span className="obd-dashboard-section-rail__icon-wrap" aria-hidden>
                            {sectionRailExpanded ? (
                                <CaretUpIcon size={19} weight="bold" />
                            ) : (
                                <CaretDownIcon size={19} weight="bold" />
                            )}
                        </span>
                    </button>
                )}
            </div>
        </nav>
    );
};
